import { axios } from '@/utils/axios';
import { isArrayOfType } from '@/utils/check';
import type { PolicyApiResponse, PolicyLocale } from '@/types/policy';
import { getPasswordResetRetryAfter, startPasswordResetCooldown } from '@/utils/passwordResetCooldown';

/**
 * ANCHOR 會員登入
 */
export const loginMember = async (data: MemberSignInFormRequest) => {
  if (process.env.REACT_APP_AUTH_OTP_V2_ENABLED === 'true') {
    const bindingIntentId =
      window.sessionStorage.getItem('external.bindingIntentId') ||
      window.sessionStorage.getItem('wechat.bindingIntentId') ||
      undefined;
    const startFlow = () =>
      axios.post<AuthFlowStartResponse>('/api/auth/login/start', {
        email: data.email,
        password: data.password,
        turnstile_token: data.turnstile_token,
        binding_intent_id: bindingIntentId,
      });
    let started: Awaited<ReturnType<typeof startFlow>>;
    try {
      started = await startFlow();
    } catch (error: any) {
      if (bindingIntentId && error.response?.status === 422) {
        window.sessionStorage.removeItem('wechat.bindingIntentId');
        window.sessionStorage.removeItem('external.bindingIntentId');
        window.sessionStorage.removeItem('external.pendingBindingIntentId');
      }
      throw error;
    }
    const factor = started.data.data.factors.find((item) => item.type === data.otp_method);
    if (!factor)
      throw new Error(`No ${data.otp_method.toUpperCase()} verification factor is available for this account.`);

    const issued = await axios.post<AuthFlowIssueResponse>(`/api/auth/login/${started.data.data.flow_id}/otp`, {
      factor_id: factor.id,
    });
    window.sessionStorage.setItem(`auth.flow.${issued.data.data.challenge_id}`, started.data.data.flow_id);
    window.sessionStorage.setItem(`auth.factor.${issued.data.data.challenge_id}`, String(factor.id));

    return issued as unknown as Awaited<ReturnType<typeof legacyLoginMember>>;
  }

  return legacyLoginMember(data);
};

const legacyLoginMember = async (data: MemberSignInFormRequest) => {
  const bindingIntentId =
    window.sessionStorage.getItem('external.bindingIntentId') ||
    window.sessionStorage.getItem('wechat.bindingIntentId') ||
    undefined;
  try {
    return await axios.post<MemberSignInStep1Response>('/api/auth/login', {
      ...data,
      binding_intent_id: bindingIntentId,
    });
  } catch (error: any) {
    if (bindingIntentId && error.response?.status === 422) {
      window.sessionStorage.removeItem('wechat.bindingIntentId');
      window.sessionStorage.removeItem('external.bindingIntentId');
      window.sessionStorage.removeItem('external.pendingBindingIntentId');
    }
    throw error;
  }
};

export const resendLoginCode = async (token: string) => {
  if (process.env.REACT_APP_AUTH_OTP_V2_ENABLED !== 'true') {
    return axios.post<MemberSignInStep1Response>(
      '/api/auth/resendCode',
      { one_time_token: token },
      { skipAuthRefresh: true }
    );
  }
  const flowId = window.sessionStorage.getItem(`auth.flow.${token}`);
  const factorId = Number(window.sessionStorage.getItem(`auth.factor.${token}`));
  if (!flowId || !Number.isSafeInteger(factorId) || factorId < 1) throw new Error('Please sign in again.');
  const response = await axios.post<AuthFlowIssueResponse>(
    `/api/auth/login/${flowId}/otp`,
    { factor_id: factorId },
    { skipAuthRefresh: true }
  );
  if (
    !response.data.success ||
    (response.data.data.submission_status && response.data.data.submission_status !== 'provider_submitted')
  ) {
    throw new Error('Verification delivery is unavailable. Please try again.');
  }
  window.sessionStorage.setItem(`auth.flow.${response.data.data.challenge_id}`, flowId);
  window.sessionStorage.setItem(`auth.factor.${response.data.data.challenge_id}`, String(factorId));
  window.sessionStorage.removeItem(`auth.flow.${token}`);
  window.sessionStorage.removeItem(`auth.factor.${token}`);
  return response;
};

/**
 * ANCHOR 會員登入二階段驗證
 */
export const loginToken = async (data: MemberSignInTokenFormRequest) => {
  if (process.env.REACT_APP_AUTH_OTP_V2_ENABLED === 'true') {
    const flowId = window.sessionStorage.getItem(`auth.flow.${data.one_time_token}`);
    if (!flowId) throw new Error('The secure login flow has expired. Please sign in again.');
    const response = await axios.post<MemberSignInResponse>(`/api/auth/login/${flowId}/verify`, {
      challenge_id: data.one_time_token,
      code: data.code,
    });
    window.sessionStorage.removeItem(`auth.flow.${data.one_time_token}`);
    window.sessionStorage.removeItem(`auth.factor.${data.one_time_token}`);
    window.sessionStorage.removeItem('wechat.bindingIntentId');
    window.sessionStorage.removeItem('external.bindingIntentId');
    window.sessionStorage.removeItem('external.pendingBindingIntentId');
    return response;
  }
  const bindingIntentId =
    window.sessionStorage.getItem('external.bindingIntentId') ||
    window.sessionStorage.getItem('wechat.bindingIntentId') ||
    undefined;
  const response = await axios.post<MemberSignInResponse>('/api/auth/verifyCode', {
    ...data,
    binding_intent_id: bindingIntentId,
  });
  window.sessionStorage.removeItem('wechat.bindingIntentId');
  window.sessionStorage.removeItem('external.bindingIntentId');
  window.sessionStorage.removeItem('external.pendingBindingIntentId');
  return response;
};

export const startWeChatOAuth = (returnPath?: string | null, turnstileToken?: string | null) =>
  axios.post<{ success: boolean; data: { authorization_url: string }; message: string }>('/api/auth/wechat/start', {
    ...(returnPath ? { return_path: returnPath } : {}),
    ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
  });

export const completeWeChatOAuth = (data: { code: string; state: string }) =>
  axios.post<WeChatOAuthCallbackResponse>('/api/auth/wechat/callback', data);

/**
 * ANCHOR 檢查Email是否重複
 */
export const checkEmailUnique = async (data: { email: string }) => {
  return axios.post<CheckEmailUniqueResponse>('/api/registration/checkEmailUnique', data);
};

/**
 * ANCHOR 送出Email驗證信
 */
export const sendEmailVerificationCode = async (data: { email: string }) => {
  return axios.post('/api/registration/register', data);
};

/**
 * ANCHOR 重新寄送Email驗證信
 */
export const resendEmailVerificationCode = async (data: { email: string }) => {
  return axios.post('/api/registration/resendVerificationCode', data);
};

/**
 * ANCHOR 驗證Email及驗證碼
 */
export const registerVerification = async (data: RegisterVerificationRequest) => {
  return axios.post('/api/registration/registerVerification', data);
};

/**
 * ANCHOR 刷新token
 */
export const refreshToken = async (token: string) => {
  return axios.post('/api/auth/refresh', token, {
    skipAuthRefresh: true,
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getAuthApplications = async () => {
  return axios.get<{ success: boolean; data: UserApplicationContext; message: string }>('/api/auth/applications');
};

/**
 * ANCHOR 取得用戶資料
 */
export const getUserDetail = async () => {
  return await axios.get<{ data: MemberProfile }>('/api/account/userDetail');
};

/**
 * ANCHOR 取得用戶產品資料
 */
export const getUserDetailWithProduct = async () => {
  return await axios.get<{ data: { products: MemberProfileProduct[] } }>('/api/account/userDetailWithProd');
};

/**
 * ANCHOR 取得用戶資料驗證
 */
export const getUserProgress = async () => {
  return await axios.get('/api/account/verificationProgress');
};

/**
 * ANCHOR 會員登出
 */
export const logout = async (data: { token: string }) => {
  return axios.post('/api/auth/logout', data);
};

/**
 * ANCHOR 忘記密碼-寄驗證信
 */
export const forgotPassword = async (data: { email: string }) => {
  try {
    const response = await axios.post<{
      success: boolean;
      data: { flowId: string; challengeId: string; expiresIn: number };
    }>('/api/account/password-reset/start', data);
    if (response.data.success) {
      window.sessionStorage.setItem('passwordReset.flowId', response.data.data.flowId);
      window.sessionStorage.setItem('passwordReset.challengeId', response.data.data.challengeId);
      startPasswordResetCooldown();
    }
    return response;
  } catch (error) {
    const retryAfter = getPasswordResetRetryAfter(error);
    if (retryAfter !== null) startPasswordResetCooldown(retryAfter);
    throw error;
  }
};

/**
 * ANCHOR 忘記密碼-送出驗證碼
 */
export const forgotPasswordVerification = async (data: { email: string; verificationCode: string }) => {
  const flowId = window.sessionStorage.getItem('passwordReset.flowId');
  const challengeId = window.sessionStorage.getItem('passwordReset.challengeId');
  if (!flowId || !challengeId) throw new Error('Password reset flow has expired.');
  const response = await axios.post<{
    success: boolean;
    data: { flowId: string; resetToken: string; expiresIn: number; resetPasswordToken: string };
  }>('/api/account/password-reset/verify', {
    flowId,
    challengeId,
    verificationCode: data.verificationCode,
  });
  response.data.data.resetPasswordToken = response.data.data.resetToken;
  window.sessionStorage.setItem('passwordReset.resetToken', response.data.data.resetToken);
  return response;
};

/**
 * ANCHOR 忘記密碼-重設密碼
 */
export const resetPassword = async (data: ResetPasswordResponse) => {
  const flowId = window.sessionStorage.getItem('passwordReset.flowId');
  const resetToken = window.sessionStorage.getItem('passwordReset.resetToken') || data.resetPasswordToken;
  if (!flowId || !resetToken) throw new Error('Password reset flow has expired.');
  const response = await axios.post('/api/account/password-reset/complete', {
    flowId,
    resetToken,
    password: data.password,
    passwordConfirmation: data.passwordConfirmation,
  });
  window.sessionStorage.removeItem('passwordReset.flowId');
  window.sessionStorage.removeItem('passwordReset.challengeId');
  window.sessionStorage.removeItem('passwordReset.resetToken');
  return response;
};

/**
 * ANCHOR 黃頁列表資料
 */
export const getYellowPageSearch = async (queryString: string, config?: any) => {
  return axios.get(`/api/yellowPage${queryString}`, config);
};

/**
 * ANCHOR 黃頁內頁資料
 */
export const getYellowPageDetail = async (data: { id: string }) => {
  return axios.get(`/api/yellowPage/${data.id}`);
};

/**
 * ANCHOR 註冊第一步 FinishRegistAccount
 */
export const registrationAccount = async (data: RegistrationAccountRequest) => {
  return axios.post<RegistrationAccountResponse>('/api/registration/finishAccountRegister', data);
};
export interface IndividualRegistrationRequest {
  email: string;
  acc_firstName: string;
  acc_lastName: string;
  password: string;
  registration_token: string;
  tcAgreement: boolean;
  PICsAgreement: boolean;
  dataTransferAgreement: boolean;
}
export const registrationIndividual = (data: IndividualRegistrationRequest) =>
  axios.post<{
    success: boolean;
    data: { access_token: string; expires_in: number; user: MemberProfile; applications?: UserApplicationContext };
  }>('/api/registration/finishIndividualRegister', data);

/**
 * ANCHOR 註冊第一步名片 OCR
 */
export const scanRegistrationBusinessCard = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return axios.post<BusinessCardOcrResponse>('/api/registration/business-card/ocr', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const revokeRegistrationBusinessCard = async (token: string) => {
  return axios.delete('/api/registration/business-card/uploads', {
    data: { business_card_upload_token: token },
  });
};

/**
 * ANCHOR 註冊第一步 UpdateRegistAccount
 */
export const updateAccount = async (data: UpdateAccountRequest) => {
  return axios.post<{ success: boolean; data: MemberProfile }>('/api/registration/updateAccountRegister', data);
};

/**
 * ANCHOR 註冊第一步 套用現有公司資料
 */
export const registerCompanyInfoWithParentCompany = async (data: { parent_company_id: string }) => {
  return axios.post('/api/account/registerCompanyInfoWithParentCompany', data);
};

/**
 * ANCHOR 註冊第二步 Company
 */
export const registrationCompany = async (data: RegistrationCompanyRequest) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      if (isArrayOfType(value, 'number')) {
        formData.append(key, value.join(','));
      } else {
        // 將字串陣列放入formData
        value.forEach((v, index) => {
          formData.append(`${key}[${index}]`, v);
        });
      }
    } else if (value) {
      formData.append(key, value);
    }
  }

  return axios.post('/api/account/registerCompanyInfo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const registrationPersonalInfo = async (data: RegistrationPersonalRequest) => {
  const postData = { ...data, TC_Promotion: '1' };

  const formData = new FormData();
  for (const [key, value] of Object.entries(postData)) {
    if (Array.isArray(value)) {
      if (isArrayOfType(value, 'number')) {
        formData.append(key, value.join(','));
      } else if (value.length > 0) {
        const products = value as RegistrationCompanyRequestProduct[];
        products.forEach((product, index) => {
          for (const [pKey, pValue] of Object.entries(product)) {
            if (pValue) formData.append(`${key}[${index}][${pKey}]`, pValue);
          }
        });
      }
    } else if (value) {
      formData.append(key, value);
    }
  }

  return axios.post('/api/account/updatePersonalInfo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const registrationSupplementalInfo = async (data: RegistrationSupplementalInfoRequest) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(data)) {
    if (value) formData.append(key, value);
  }
  return axios.post('/api/account/registerSupplementalInfo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const registrationECert = async (data: File) => {
  const formData = new FormData();
  if (data) formData.append('e_cert', data);
  return axios.post('/api/account/updateECert', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const registrationPRC = async (data: RegistrationPRCRequest) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(data)) {
    if (value) formData.append(key, value);
  }
  return axios.post('/api/account/updatePRC', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// 確認資料無誤後送出
export const registerPreview = async (data: { TC_Promotion: string }) => {
  return axios.post('/api/account/registerPreview', data);
};

// 刪除上傳的檔案
export const removeFile = async (data: { fileType: string }) => {
  return axios.post('/api/account/removeFile', data);
};

// 切換使用者語系
export const switchLang = async (data: { lang: string }) => {
  return axios.post('/api/account/switchLanguage', data);
};

// 取得子帳號列表
export const getSubAccountList = async () => {
  return axios.get('/api/account/getSubAccountList');
};

// 寄出成為子帳號的邀請信
export const addSubAccountSendMails = async (data: { emails: string }) => {
  return axios.post('/api/account/addSubAccountSendMails', data);
};

// 完成子帳號的註冊
export const finishSubAccount = async (data: RegistrationAccountRequest) => {
  return axios.post<{
    success: boolean;
    data: {
      access_token: string;
      expires_in: number;
      user: MemberProfile;
      applications?: UserApplicationContext;
    };
  }>('/api/account/finishSubAccount', data);
};

// 編輯子帳號
export const editSubAccount = async (id: string, data: Omit<UpdateAccountRequest, 'role'>) => {
  return axios.post(`/api/account/editSubAccount/${id}`, data);
};

// 刪除子帳號
export const deleteSubAccount = async (id: string) => {
  return axios.post(`/api/account/deleteSubAccount/${id}`);
};

// 取得子帳號資料
export const getSubAccountDetail = async (id: string) => {
  return axios.get(`/api/account/getSubAccountInfo/${id}`);
};

// 確認檔案容量
export const getStorageSpace = async () => {
  return axios.get('/api/account/getStorageSpace2');
};

// 取得該帳號的子帳號數量
export const getMembers = async () => {
  return axios.get('/api/account/members');
};

// 取得使用者條款文件
export const getPolicy = async (locale?: PolicyLocale) => {
  return axios.get<PolicyApiResponse>('/api/policy/getPolicy', { params: locale ? { locale } : undefined });
};

// 將帳號轉為子帳號
export const addUsetToSubAccount = async (data: AddUserToSubAccountRequest) => {
  return axios.post('/api/registration/addUserToSubAccount', data);
};

// 取得新的Policy條款文件
export const getNewPolicy = async () => {
  return axios.post('/api/policy/getPolicy');
};

// 使用者同意新的Policy條款
export const updateAgreementsAcceptedAt = async (data: UpgradeAgreementsAcceptedAtRequired) => {
  return axios.post('/api/account/updateAgreementsAcceptedAt', data);
};

// SMS註冊
export const smsRegister = async (data: SMSRegisterRequired) => {
  return axios.post('/api/registration/register', data);
};

// SMS發送驗證碼
export const sendSMSCode = async (data: SMSVerificationRequired) => {
  return axios.post('/api/registration/registerVerification', data);
};

// 提交Goglobal申請
export const submitGoGlobalApplication = async (data: GoGlobalApplicationRequest) => {
  return axios.post('/api/account/applyGoGlobal', data);
};

// 取得自己的GoGlobal申請資料
export const getGoGlobalApplication = async () => {
  return axios.get('/api/account/goGlobalApplication');
};

export const sendRegistrationVerificationCode = (data: SMSRegisterRequired) => axios.post('/api/registration/register', data);
export const resendRegistrationVerificationCode = (data: SMSRegisterRequired) => axios.post('/api/registration/resendVerificationCode', data);
